import {MoreOutlined} from "@ant-design/icons";
import {Button, Dropdown, Menu, message} from "antd";
import {
  useDeleteUserScheduleApi,
  useDuplicateUserScheduleApi,
  useGetUserSchedules,
  useSetDefaultUserScheduleApi,
} from "../../../../../../../react-query-hooks/schedule";
import {useState} from "react";
import {RenameScheduleFormModal} from "../../rename-schedule-form";
import {AssignSessionsToScheduleOnDelete} from "../../apply-schedule-to-session-modal";

export type UserScheduleItemMenuProps = {
  scheduleId: string;
  scheduleName: string;
  isDefault: boolean;
  numberOfSessions: number;
  handleOpenApplyScheduleToSessionModal(): void;
};

export function UserScheduleItemMenu({
  scheduleId,
  scheduleName,
  isDefault,
  numberOfSessions,
  handleOpenApplyScheduleToSessionModal,
}: UserScheduleItemMenuProps) {
  const [isRenameScheduleModalOpen, setIsRenameScheduleModalOpen] = useState(false);
  const [isDeleteScheduleModalOpen, setIsDeleteScheduleModalOpen] = useState(false);
  const setDefaultUserScheduleApi = useSetDefaultUserScheduleApi();

  const duplicateUserScheduleApi = useDuplicateUserScheduleApi();
  const deleteUserScheduleApi = useDeleteUserScheduleApi();
  const getUserSchedulesQuery = useGetUserSchedules();

  function handleSetAsDefault() {
    setDefaultUserScheduleApi.mutate(
      {
        scheduleId,
      },
      {
        onSuccess() {
          message.success(`${scheduleName} has been set as default successfully`);
        },
      }
    );
  }

  function handleDeleteUserSchedule() {
    // When the schedule has no sessions attached, there is nothing to reassign,
    // so skip the "Select a new schedule for this session" modal and delete
    // directly. The delete endpoint still requires a valid newScheduleId (an
    // empty one 500s), but nothing is actually re-homed — so we hand it any
    // other existing schedule, preferring the default.
    if (numberOfSessions <= 0) {
      const otherSchedules =
        getUserSchedulesQuery.data?.schedules.filter((s) => s.id !== scheduleId) ?? [];
      const fallbackScheduleId =
        otherSchedules.find((s) => s.isDefault)?.id ?? otherSchedules[0]?.id;

      if (!fallbackScheduleId) {
        message.error("Unable to delete this schedule right now. Please try again.");
        return;
      }

      deleteUserScheduleApi.mutate(
        {scheduleId, newScheduleId: fallbackScheduleId},
        {
          onSuccess() {
            message.success(`${scheduleName} has been deleted successfully`);
          },
        }
      );
      return;
    }

    setIsDeleteScheduleModalOpen(true);
  }

  function handleDuplicateUserSchedule() {
    duplicateUserScheduleApi.mutate(
      {
        scheduleId,
      },
      {
        onSuccess() {
          message.success(`${scheduleName} has been duplicated successfully`);
        },
      }
    );
  }

  function handleOpenRenameScheduleModal() {
    setIsRenameScheduleModalOpen(true);
  }
  function handleCloseRenameScheduleModal() {
    setIsRenameScheduleModalOpen(false);
  }
  function handleRenameScheduleModalOk() {
    setIsRenameScheduleModalOpen(false);
  }
  function handleCloseDeleteScheduleModal() {
    setIsDeleteScheduleModalOpen(false);
  }

  const menu = (
    <Menu>
      <Menu.Item
        key="Apply schedule to session(s)"
        onClick={handleOpenApplyScheduleToSessionModal}
      >
        Apply schedule to session(s)
      </Menu.Item>
      <Menu.Item
        key="Set schedule as default"
        disabled={isDefault}
        onClick={handleSetAsDefault}
      >
        Set schedule as default
      </Menu.Item>
      <Menu.Item key="Rename schedule" onClick={handleOpenRenameScheduleModal}>
        Rename schedule
      </Menu.Item>
      <Menu.Item key="Duplicate schedule" onClick={handleDuplicateUserSchedule}>
        Duplicate schedule
      </Menu.Item>
      <Menu.Item
        key="Delete"
        danger
        disabled={isDefault}
        onClick={handleDeleteUserSchedule}
      >
        Delete
      </Menu.Item>
    </Menu>
  );
  return (
    <>
      <Dropdown overlay={menu}>
        <Button icon={<MoreOutlined />} shape="circle" type="text" size="small" />
      </Dropdown>
      <RenameScheduleFormModal
        handleCloseRenameScheduleModal={handleCloseRenameScheduleModal}
        handleRenameScheduleModalOk={handleRenameScheduleModalOk}
        isRenameScheduleModalOpen={isRenameScheduleModalOpen}
        scheduleId={scheduleId}
        scheduleName={scheduleName}
      />
      <AssignSessionsToScheduleOnDelete
        closeAssignSessionsToScheduleOnDeleteModal={handleCloseDeleteScheduleModal}
        isAssignSessionsToScheduleOnDeleteModalOpen={isDeleteScheduleModalOpen}
        scheduleId={scheduleId}
        scheduleName={scheduleName}
      />
    </>
  );
}
